import * as React from "react";
import { BaseDropdownProps, findPathInGroup, isGroup, ItemPath } from "designer/lib/dropdown-types";
import GlideDropdownButton from "./private/glide-dropdown-button";
import { useLayer } from "react-laag";
import { PushDropdown } from "notebook/push-dropdown/push-dropdown";
import { defined, isEmptyOrUndefined, nonNull } from "common/Support";
import { getNamedIcon } from "common/named-icons";
import {
    SplitContainer,
    SplitDropdownContainer,
    RemoveIconContainer,
} from "designer/builder/ui/views/split-container/split-container";
import PropertyTitle from "designer/ui/views/property-title/property-title";
import { DropdownLabel, GlideDropdownWrapper, IconWrapper } from "./glide-dropdown-style";
import { Tooltip } from "../tooltip/tooltip";
import HighlightTextArea from "sharedUI/ui/smart/highlight-textarea/highlight-textarea";
import classNames from "classnames";
import { styled } from "sharedUI/styles";

export interface Props<T> extends Omit<BaseDropdownProps<T>, "width"> {
    readonly displayLabel?: string;
    readonly isEnabled?: boolean;
    readonly label?: string;
    readonly width?: string;
    readonly helpUrl?: string;
    readonly helpText?: string;

    readonly onRemove?: () => void;

    readonly slim?: boolean;
    readonly isInline?: boolean;
    readonly tallerVariation?: boolean;
    readonly showWarning?: boolean;
    readonly warnMessage?: string;
    readonly defaultDisplayLabel?: string;
    readonly drawBorder?: boolean;
    readonly highlight?: boolean;
    readonly highlightLabel?: boolean;

    readonly showBreadcrumb?: boolean;
    readonly customInputValue?: string;
    readonly customInputPlaceholder?: string;
    readonly onCustomInputChanged?: (v: string) => void;
    readonly validateCustomValue?: (v: string) => boolean;
}

const Wrapper = styled.div`
    &.prelight {
        box-shadow: 0 0 4px ${p => p.theme.b400};
        border-radius: 8px;
    }
`;

export function GlideDropdown<T>(p: Props<T>): React.ReactElement | null {
    const {
        displayLabel,
        onRemove,
        isEnabled,
        helpUrl,
        helpText,
        label,
        width,
        slim,
        isInline,
        tallerVariation,
        warnMessage,
        showWarning,
        showBreadcrumb,
        defaultDisplayLabel,
        highlight,
        highlightLabel,
        drawBorder,
        customInputPlaceholder,
        customInputValue,
        onCustomInputChanged,
        descriptionForItem,
        validateCustomValue,
        ...rest
    } = p;
    const { selected, items } = rest;

    const [menuOpen, setMenuOpen] = React.useState(false);

    let displayValue: { name: string; icon: React.ReactNode }[] | undefined;

    if (selected !== undefined) {
        const path = findPathInGroup(items, selected);

        if (path === undefined || showBreadcrumb === false) {
            const description = descriptionForItem(selected);
            if (typeof description === "string") {
                displayValue = [{ name: description, icon: undefined }];
            } else {
                displayValue = [
                    {
                        name: description.selectedName ?? description.name,
                        icon: description.selectedIcon ?? description.icon,
                    },
                ];
            }
        } else {
            displayValue = path.map(partial => {
                if (isGroup(partial)) {
                    return { name: partial.name, icon: partial.icon };
                }
                const desc = descriptionForItem(partial);
                if (typeof desc === "string") {
                    return { name: desc, icon: undefined };
                } else {
                    return { name: desc.selectedName ?? desc.name, icon: desc.selectedIcon ?? desc.icon };
                }
            });
        }
    }

    if (displayValue === undefined) {
        displayValue = [{ name: defaultDisplayLabel ?? "None", icon: undefined }];
    }

    const onClickOutside = React.useCallback(() => {
        setMenuOpen(false);
    }, []);

    const isOpen = items.length > 0 && menuOpen;
    const { layerProps, renderLayer, triggerProps, triggerBounds } = useLayer({
        isOpen,
        container: nonNull(document.getElementById("portal"), 'No element with id="portal" found'),
        placement: "bottom-end",
        possiblePlacements: ["bottom-start", "top-start", "bottom-end", "top-end"],
        auto: true,
        containerOffset: 2,
        overflowContainer: true,
        onOutsideClick: onClickOutside,
    });

    const onButtonClick = React.useCallback(() => {
        setMenuOpen(c => !c);
    }, []);

    const onItemSelect = React.useCallback(
        (item: T, path: ItemPath) => {
            rest.onItemSelect?.(item, path);
            setMenuOpen(false);
        },
        [rest]
    );

    const maxHeight = React.useMemo((): number | undefined => {
        if (triggerBounds === null) return undefined;
        const windowHeight = window.innerHeight;
        return Math.max(windowHeight - triggerBounds.bottom, triggerBounds.top) - 16;
    }, [triggerBounds]);

    const highlightAreaRef = React.useRef<HTMLTextAreaElement>(null);
    const first = React.useRef(true);
    const hasInputValue = customInputValue !== undefined;
    React.useEffect(() => {
        if (first.current) {
            first.current = false;
            return;
        }

        if (hasInputValue && highlightAreaRef.current !== null) {
            highlightAreaRef.current.focus();
        }
    }, [hasInputValue]);

    let dropdown = (
        <>
            <Wrapper
                {...triggerProps}
                style={{ width: "100%" }}
                className={classNames(highlight === true && "prelight")}>
                {hasInputValue && (
                    // FIXME: Honor `drawBorder` here, too
                    <div className={classNames("highlight-container", drawBorder === false && "no-border")}>
                        <HighlightTextArea
                            inputRef={highlightAreaRef}
                            placeholder={customInputPlaceholder}
                            value={customInputValue}
                            validate={validateCustomValue}
                            onChange={e => {
                                defined(onCustomInputChanged)(e.target.value);
                            }}
                            debounceUpdate={true}
                            onContextButton={onButtonClick}
                        />
                    </div>
                )}
                {!hasInputValue && (
                    <GlideDropdownButton
                        value={displayValue}
                        isOpen={isOpen}
                        warn={showWarning === true}
                        warnString={warnMessage}
                        drawBorder={drawBorder}
                        // onKeyDown={this.onKeyDown}
                        onClick={onButtonClick}
                        isEnabled={items.length > 0}
                        slim={slim}
                        isInline={isInline}
                        tallerVariation={tallerVariation}
                    />
                )}
            </Wrapper>
            {isOpen &&
                renderLayer(
                    <div {...layerProps}>
                        <PushDropdown
                            height={maxHeight}
                            width={triggerBounds?.width}
                            descriptionForItem={descriptionForItem}
                            prefer={
                                triggerBounds === null
                                    ? "right"
                                    : window.innerWidth - triggerBounds.right < 200
                                    ? "left"
                                    : "right"
                            }
                            {...rest}
                            onItemSelect={onItemSelect}
                        />
                    </div>
                )}
        </>
    );

    const ref = React.useRef<any>();

    if (!isEmptyOrUndefined(displayLabel)) {
        const HelpIcon = getNamedIcon("helpIcon");
        const RemoveIcon = getNamedIcon("remove");
        dropdown = (
            <SplitContainer>
                <DropdownLabel>
                    <PropertyTitle title={displayLabel} highlight={highlightLabel} />
                    {helpUrl !== undefined && (
                        <div>
                            <IconWrapper ref={ref} href={helpUrl} target={"_blank"} rel="noopener noreferrer">
                                <HelpIcon />
                            </IconWrapper>
                            {helpText !== undefined && (
                                <Tooltip position="bottom-left" target={ref} width={260}>
                                    {helpText}
                                </Tooltip>
                            )}
                        </div>
                    )}
                </DropdownLabel>
                <SplitDropdownContainer>
                    {dropdown}
                    {onRemove !== undefined && (
                        <RemoveIconContainer onClick={onRemove}>
                            <RemoveIcon />
                        </RemoveIconContainer>
                    )}
                </SplitDropdownContainer>
            </SplitContainer>
        );
    }

    return (
        <GlideDropdownWrapper
            className={classNames(slim && "slim")}
            isEnabled={isEnabled ?? true}
            data-testid={`glide-dropdown-${displayLabel ?? label ?? "unlabeled"}`}>
            {label !== undefined && <div className="dropdown-label">{label}</div>}
            {dropdown}
        </GlideDropdownWrapper>
    );
}
